import React, { useState, useEffect, useRef } from 'react';
import { Camera, Search, ChevronDown, ChevronUp } from 'lucide-react';
import MultiImageUpload from './MultiImageUpload';
import { useMediaDataContext } from '../contexts/MediaDataContext';
import { useNotification } from '../contexts/NotificationContext';
import { useForm } from '../contexts/FormContext';
import BaseModal from './BaseModal';
import { LoadingButton } from './LoadingStates';
import { sanitizeText, sanitizeFormData } from '../utils/security';
import { VALIDATION_RULES } from '../utils/constants';
import CustomSelect from './CustomSelect';

const AddMediaModal = ({
  isOpen,
  onClose,
  editingMasterItem,
  selectedMasterImages,
  setSelectedMasterImages,
  selectedReleaseImages,
  setSelectedReleaseImages,
  addToMyCollection,
  setAddToMyCollection,
  addToMyWantlist,
  setAddToMyWantlist,
  releaseFormData,
  setReleaseFormData,
  // Search/scan functions
  setShowMovieSearch,
  setShowUPCScanner,
  isDataFromSearch,
  // Reset functions
  resetFormsWithImages,
  resetReleaseForm,
  // Save function
  handleSaveMovieAndRelease,
  // Admin privileges
  isAdmin,
}) => {
  const { newMasterItem, setNewMasterItem, newCollectionItem, setNewCollectionItem } =
    useMediaDataContext();
  const { showError } = useNotification();
  const form = useForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addFirstRelease, setAddFirstRelease] = useState(false); // Default to false
  const [expandedForm, setExpandedForm] = useState(null); // null | 'movie' | 'tv_show'

  // Track original API data to determine which fields should be locked
  const [originalApiData, setOriginalApiData] = useState({
    movie: {},
    release: {},
  });

  // Track whether we've captured the original data to prevent re-capturing
  const hasCapture = useRef({ movie: false, release: false });

  const handleClose = () => {
    onClose();
    setSelectedMasterImages([]);
    setSelectedReleaseImages([]);
    resetFormsWithImages();
    resetReleaseForm();
    setAddToMyCollection(true);
    setAddToMyWantlist(false);
    setAddFirstRelease(false); // Reset to default
    setExpandedForm(null);
    setOriginalApiData({ movie: {}, release: {} }); // Reset original API data
    hasCapture.current = { movie: false, release: false }; // Reset capture flags
  };

  // Capture original API data when modal opens with search data (only once)
  useEffect(() => {
    if (
      isDataFromSearch &&
      newMasterItem &&
      Object.keys(newMasterItem).length > 0 &&
      !hasCapture.current.movie
    ) {
      setOriginalApiData(prev => ({
        ...prev,
        movie: { ...newMasterItem }, // Store a copy of the original movie data
      }));
      hasCapture.current.movie = true;
    } else if (!isDataFromSearch && !editingMasterItem) {
      // For manual entry (not from search, not editing), ensure originalApiData is clean
      setOriginalApiData({ movie: {}, release: {} });
      hasCapture.current = { movie: false, release: false };
    }
  }, [isDataFromSearch, newMasterItem, editingMasterItem]);

  // Capture original release data when it's populated (only once)
  useEffect(() => {
    if (
      isDataFromSearch &&
      releaseFormData &&
      Object.keys(releaseFormData).length > 0 &&
      !hasCapture.current.release
    ) {
      setOriginalApiData(prev => ({
        ...prev,
        release: { ...releaseFormData }, // Store a copy of the original release data
      }));
      hasCapture.current.release = true;
    }
  }, [isDataFromSearch, releaseFormData]);

  // Auto-expand the right form panel when search data comes in
  useEffect(() => {
    if (isDataFromSearch && newMasterItem.type) {
      setExpandedForm(newMasterItem.type === 'tv_show' ? 'tv_show' : 'movie');
    }
  }, [isDataFromSearch, newMasterItem.type]);

  // Auto-expand when editing an existing item
  useEffect(() => {
    if (editingMasterItem) {
      setExpandedForm(editingMasterItem.type === 'tv_show' ? 'tv_show' : 'movie');
    }
  }, [editingMasterItem]);

  const toggleForm = formType => {
    const next = expandedForm === formType ? null : formType;
    setExpandedForm(next);
    if (next && !isDataFromSearch && !editingMasterItem) {
      setNewMasterItem({ ...newMasterItem, type: next });
    }
  };

  // Sanitization helpers
  const sanitizeAndUpdateMasterItem = (field, value) => {
    let sanitizedValue;

    if (field === 'description') {
      // Allow some HTML in descriptions but sanitize it
      sanitizedValue = sanitizeText(value).substring(0, VALIDATION_RULES.DESCRIPTION_MAX_LENGTH);
    } else {
      // Plain text fields
      sanitizedValue = sanitizeText(value).substring(0, VALIDATION_RULES.TITLE_MAX_LENGTH);
    }

    setNewMasterItem({ ...newMasterItem, [field]: sanitizedValue });
  };

  // Helper function to check if a field is empty/null and can be edited
  const isFieldEditable = fieldName => {
    if (!editingMasterItem) return true; // Always editable when adding new items

    // ADMIN OVERRIDE: Admins can edit all fields regardless of existing content
    if (isAdmin) return true;

    // Check the original value from editingMasterItem, not the current form value
    const originalValue = editingMasterItem[fieldName];
    return !originalValue || originalValue.toString().trim() === '';
  };

  // Helper function to check if a movie field should be read-only
  const isMovieFieldReadOnly = fieldName => {
    // For editing existing items, use the original logic
    if (editingMasterItem) {
      return !isFieldEditable(fieldName);
    }

    // For new items from search/UPC: only lock fields that originally came with data from the API
    if (isDataFromSearch) {
      const originalValue = originalApiData.movie[fieldName];
      return originalValue && originalValue.toString().trim() !== '';
    }

    // For manual entries (including "Add First Release" mode), all fields are always editable
    return false;
  };

  const handleSave = async () => {
    // Validate release title is required only if adding first release
    if (
      addFirstRelease &&
      (!releaseFormData.release_title || !releaseFormData.release_title.trim())
    ) {
      showError('Release title is required when adding first release');
      return;
    }

    setIsSubmitting(true);
    try {
      // Sanitize all form data before saving
      const sanitizedMasterItem = sanitizeFormData(
        newMasterItem,
        ['title', 'director', 'actors', 'writer', 'country', 'language', 'rated', 'genre'],
        ['description']
      );

      const sanitizedReleaseData = sanitizeFormData(
        releaseFormData,
        [
          'release_title',
          'studio',
          'case_type',
          'release_country',
          'release_year',
          'upc',
          'upc_distributor',
          'distributor_code',
          'region',
          'format',
          'speed',
          'edition',
          'edition_other',
        ],
        ['release_notes']
      );

      // Update the state with sanitized data
      setNewMasterItem(sanitizedMasterItem);
      setReleaseFormData(sanitizedReleaseData);

      const result = await handleSaveMovieAndRelease(addFirstRelease);

      if (editingMasterItem && result?.success && result?.movieData) {
        form.setEditingMasterItem(result.movieData);
      }

      handleClose();
    } catch (error) {
      console.error('❌ Error saving movie and release:', error);
      showError('Error saving movie: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        closeOnBackdrop={false}
        title={
          editingMasterItem
            ? editingMasterItem.type === 'tv_show'
              ? 'Edit TV Show Details'
              : 'Edit Movie Details'
            : 'Add New Title to Catalog (with optional release)'
        }
        maxWidth="2xl"
      >
        {/* Admin Mode Indicator */}
        {isAdmin && editingMasterItem && (
          <div className="bg-yellow-600 text-yellow-100 p-3 rounded-lg mb-4 text-sm flex items-center space-x-2">
            <span className="text-yellow-200">⚠️</span>
            <span>
              <strong>Admin Mode:</strong> You can edit all fields including those with existing
              data
            </span>
          </div>
        )}

        {/* Step 1: Title Information */}
        <div className="mb-6">
          {/* Quick Search - only for new items */}
          {!editingMasterItem && (
            <div className="mb-4 p-4 bg-black border border-gray-600 rounded-lg">
              <h4 className="text-md font-semibold text-white mb-1">Quick Search</h4>
              <p className="text-gray-300 text-sm mb-4">
                Search online databases to automatically fill title details and poster.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => setShowMovieSearch(true)}
                  className="bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2 transition-colors"
                >
                  <Search size={20} />
                  <span>Search Movies and TV</span>
                </button>
                <button
                  onClick={() => setShowUPCScanner(true)}
                  className="bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2 transition-colors"
                >
                  <Camera size={20} />
                  <span>Scan UPC</span>
                </button>
              </div>
            </div>
          )}

          {isDataFromSearch && !editingMasterItem && (
            <div className="mb-4 p-3 bg-black border border-gray-600 rounded-lg space-y-2">
              <p className="text-sm text-gray-300">
                <span className="font-medium">API Data Protected:</span> Title information has been
                populated from search results and is read-only to maintain data accuracy.
              </p>
              <p className="text-sm text-gray-300">
                Posters will be automatically retrieved from the database where available. You can
                add additional posters later when editing.
              </p>
            </div>
          )}

          {editingMasterItem && (
            <div className="mb-4 p-3 bg-black border border-gray-600 rounded-lg">
              <p className="text-sm text-gray-300">
                <strong>Edit Mode:</strong> You can edit empty fields and update posters. Fields
                with existing data are protected to maintain data integrity. Other edits can be left
                in the comments and voted on by community members.
              </p>
            </div>
          )}

          {/* Movie Form Panel — hidden when search/edit result is a TV show */}
          {!(isDataFromSearch && newMasterItem.type === 'tv_show') &&
            !(editingMasterItem && editingMasterItem.type === 'tv_show') && (
              <div className="mb-3 border border-gray-600 rounded-lg overflow-hidden hover:border-red-500 transition-colors">
                <button
                  type="button"
                  onClick={() => toggleForm('movie')}
                  className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors"
                >
                  <span className="font-semibold text-white">
                    {isDataFromSearch || editingMasterItem
                      ? 'Movie Details'
                      : 'Enter new Movie Manually'}
                  </span>
                  {expandedForm === 'movie' ? (
                    <ChevronUp size={18} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={18} className="text-gray-400" />
                  )}
                </button>

                {expandedForm === 'movie' && (
                  <div className="p-4 space-y-4">
                    <input
                      type="text"
                      placeholder="Movie Title *"
                      value={newMasterItem.title || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('title', e.target.value)}
                      readOnly={isMovieFieldReadOnly('title')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('title') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      required
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Director"
                        value={newMasterItem.director || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('director', e.target.value)}
                        readOnly={isMovieFieldReadOnly('director')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('director') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Producer"
                        value={newMasterItem.producer || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('producer', e.target.value)}
                        readOnly={isMovieFieldReadOnly('producer')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('producer') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Writer(s)"
                        value={newMasterItem.writer || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('writer', e.target.value)}
                        readOnly={isMovieFieldReadOnly('writer')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('writer') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Production / Distributor"
                        value={newMasterItem.distributor || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('distributor', e.target.value)}
                        readOnly={isMovieFieldReadOnly('distributor')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('distributor') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <input
                      type="text"
                      placeholder="Starring (comma separated)"
                      value={newMasterItem.actors || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('actors', e.target.value)}
                      readOnly={isMovieFieldReadOnly('actors')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('actors') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Genre"
                        value={newMasterItem.genre || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('genre', e.target.value)}
                        readOnly={isMovieFieldReadOnly('genre')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('genre') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Released * (19 Nov 1993)"
                        value={newMasterItem.released || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('released', e.target.value)}
                        readOnly={isMovieFieldReadOnly('released')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('released') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                        required
                      />
                    </div>

                    <textarea
                      placeholder="Description"
                      value={newMasterItem.description || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('description', e.target.value)}
                      rows={3}
                      readOnly={isMovieFieldReadOnly('description')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('description') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        type="text"
                        placeholder="IMDb Rating"
                        value={newMasterItem.imdb_rating || ''}
                        onChange={e => {
                          const v = sanitizeText(e.target.value)
                            .replace(/[^0-9.]/g, '')
                            .substring(0, 4);
                          setNewMasterItem({ ...newMasterItem, imdb_rating: v });
                        }}
                        readOnly={isMovieFieldReadOnly('imdb_rating')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('imdb_rating') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="MPAA Rating"
                        value={newMasterItem.rated || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('rated', e.target.value)}
                        readOnly={isMovieFieldReadOnly('rated')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('rated') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Runtime"
                        value={newMasterItem.runtime || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('runtime', e.target.value)}
                        readOnly={isMovieFieldReadOnly('runtime')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('runtime') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Budget ($)"
                        value={newMasterItem.budget || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('budget', e.target.value)}
                        readOnly={isMovieFieldReadOnly('budget')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('budget') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Box Office"
                        value={newMasterItem.boxoffice || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('boxoffice', e.target.value)}
                        readOnly={isMovieFieldReadOnly('boxoffice')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('boxoffice') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Country"
                        value={newMasterItem.country || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('country', e.target.value)}
                        readOnly={isMovieFieldReadOnly('country')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('country') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Language"
                        value={newMasterItem.language || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('language', e.target.value)}
                        readOnly={isMovieFieldReadOnly('language')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('language') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <input
                      type="url"
                      placeholder="Trailer URL"
                      value={newMasterItem.trailer_url || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('trailer_url', e.target.value)}
                      readOnly={isMovieFieldReadOnly('trailer_url')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('trailer_url') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Tagline"
                        value={newMasterItem.tagline || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('tagline', e.target.value)}
                        readOnly={isMovieFieldReadOnly('tagline')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('tagline') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Alternate Title"
                        value={newMasterItem.alternate_title || ''}
                        onChange={e =>
                          sanitizeAndUpdateMasterItem('alternate_title', e.target.value)
                        }
                        readOnly={isMovieFieldReadOnly('alternate_title')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('alternate_title') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    {(isDataFromSearch || newMasterItem.tmdb_id) && (
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="TMDB ID"
                        value={newMasterItem.tmdb_id || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('tmdb_id', e.target.value)}
                        readOnly={isMovieFieldReadOnly('tmdb_id')}
                        className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('tmdb_id') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    )}

                    <textarea
                      placeholder="Notes"
                      value={newMasterItem.notes || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('notes', e.target.value)}
                      rows={2}
                      readOnly={isMovieFieldReadOnly('notes')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('notes') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />
                  </div>
                )}
              </div>
            )}

          {/* TV Show Form Panel — hidden when search/edit result is a movie */}
          {!(isDataFromSearch && newMasterItem.type !== 'tv_show' && newMasterItem.type) &&
            !(editingMasterItem && editingMasterItem.type !== 'tv_show') && (
              <div className="mb-3 border border-gray-600 rounded-lg overflow-hidden hover:border-red-500 transition-colors">
                <button
                  type="button"
                  onClick={() => toggleForm('tv_show')}
                  className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors"
                >
                  <span className="font-semibold text-white">
                    {isDataFromSearch || editingMasterItem
                      ? 'TV Show Details'
                      : 'Enter new TV Show Manually'}
                  </span>
                  {expandedForm === 'tv_show' ? (
                    <ChevronUp size={18} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={18} className="text-gray-400" />
                  )}
                </button>

                {expandedForm === 'tv_show' && (
                  <div className="p-4 space-y-4">
                    <input
                      type="text"
                      placeholder="Show Title *"
                      value={newMasterItem.title || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('title', e.target.value)}
                      readOnly={isMovieFieldReadOnly('title')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('title') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      required
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Created by"
                        value={newMasterItem.director || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('director', e.target.value)}
                        readOnly={isMovieFieldReadOnly('director')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('director') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Executive Producer"
                        value={newMasterItem.producer || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('producer', e.target.value)}
                        readOnly={isMovieFieldReadOnly('producer')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('producer') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <input
                      type="text"
                      placeholder="Starring (comma separated)"
                      value={newMasterItem.actors || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('actors', e.target.value)}
                      readOnly={isMovieFieldReadOnly('actors')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('actors') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Genre"
                        value={newMasterItem.genre || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('genre', e.target.value)}
                        readOnly={isMovieFieldReadOnly('genre')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('genre') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Network (e.g. HBO, AMC)"
                        value={newMasterItem.network || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('network', e.target.value)}
                        readOnly={isMovieFieldReadOnly('network')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('network') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Seasons"
                        value={newMasterItem.number_of_seasons || ''}
                        onChange={e =>
                          setNewMasterItem({
                            ...newMasterItem,
                            number_of_seasons: e.target.value ? parseInt(e.target.value) : null,
                          })
                        }
                        readOnly={isMovieFieldReadOnly('number_of_seasons')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('number_of_seasons') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="Episodes"
                        value={newMasterItem.number_of_episodes || ''}
                        onChange={e =>
                          setNewMasterItem({
                            ...newMasterItem,
                            number_of_episodes: e.target.value ? parseInt(e.target.value) : null,
                          })
                        }
                        readOnly={isMovieFieldReadOnly('number_of_episodes')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('number_of_episodes') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <CustomSelect
                        title="Status"
                        value={newMasterItem.show_status || ''}
                        onChange={v => setNewMasterItem({ ...newMasterItem, show_status: v })}
                        disabled={isMovieFieldReadOnly('show_status')}
                        options={[
                          { value: '', label: 'Status' },
                          { value: 'Ended', label: 'Ended' },
                          { value: 'Returning Series', label: 'Returning Series' },
                          { value: 'Miniseries', label: 'Miniseries' },
                          { value: 'Cancelled', label: 'Cancelled' },
                          { value: 'In Production', label: 'In Production' },
                        ]}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="First Air Date * (20 Jan 2008)"
                        value={newMasterItem.released || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('released', e.target.value)}
                        readOnly={isMovieFieldReadOnly('released')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('released') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Last Air Date"
                        value={newMasterItem.last_air_date || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('last_air_date', e.target.value)}
                        readOnly={isMovieFieldReadOnly('last_air_date')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('last_air_date') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <textarea
                      placeholder="Overview / Description"
                      value={newMasterItem.description || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('description', e.target.value)}
                      rows={3}
                      readOnly={isMovieFieldReadOnly('description')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('description') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="IMDb Rating"
                        value={newMasterItem.imdb_rating || ''}
                        onChange={e => {
                          const v = sanitizeText(e.target.value)
                            .replace(/[^0-9.]/g, '')
                            .substring(0, 4);
                          setNewMasterItem({ ...newMasterItem, imdb_rating: v });
                        }}
                        readOnly={isMovieFieldReadOnly('imdb_rating')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('imdb_rating') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Content Rating (e.g. TV-MA)"
                        value={newMasterItem.rated || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('rated', e.target.value)}
                        readOnly={isMovieFieldReadOnly('rated')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('rated') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <input
                      type="text"
                      placeholder="Production Companies / Distributor"
                      value={newMasterItem.distributor || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('distributor', e.target.value)}
                      readOnly={isMovieFieldReadOnly('distributor')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('distributor') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Country"
                        value={newMasterItem.country || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('country', e.target.value)}
                        readOnly={isMovieFieldReadOnly('country')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('country') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Language"
                        value={newMasterItem.language || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('language', e.target.value)}
                        readOnly={isMovieFieldReadOnly('language')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('language') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <input
                        type="url"
                        placeholder="Trailer URL"
                        value={newMasterItem.trailer_url || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('trailer_url', e.target.value)}
                        readOnly={isMovieFieldReadOnly('trailer_url')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('trailer_url') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                      <input
                        type="text"
                        placeholder="Tagline"
                        value={newMasterItem.tagline || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('tagline', e.target.value)}
                        readOnly={isMovieFieldReadOnly('tagline')}
                        className={`px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('tagline') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    </div>

                    {(isDataFromSearch || newMasterItem.tmdb_id) && (
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="TMDB ID"
                        value={newMasterItem.tmdb_id || ''}
                        onChange={e => sanitizeAndUpdateMasterItem('tmdb_id', e.target.value)}
                        readOnly={isMovieFieldReadOnly('tmdb_id')}
                        className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('tmdb_id') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      />
                    )}

                    <textarea
                      placeholder="Notes"
                      value={newMasterItem.notes || ''}
                      onChange={e => sanitizeAndUpdateMasterItem('notes', e.target.value)}
                      rows={2}
                      readOnly={isMovieFieldReadOnly('notes')}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${isMovieFieldReadOnly('notes') ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Shared Poster Section - edit mode only */}
        {editingMasterItem && (
          <div className="mb-4 p-4 bg-black border border-gray-600 rounded-lg">
            <h5 className="text-sm font-medium text-gray-300 mb-2">
              Posters ({selectedMasterImages.length}/5)
            </h5>
            <MultiImageUpload
              onImagesChange={(images, error) => {
                setSelectedMasterImages(images);
                if (error) console.error('Poster error:', error);
              }}
              currentImages={selectedMasterImages}
              error={null}
              maxImages={5}
            />
          </div>
        )}

        {/* Step 2: Add First Release Option - Only for new movies */}
        {!editingMasterItem && (
          <div className="mb-6">
            <label
              className={`group flex items-center space-x-3 p-4 rounded-lg border mb-4 cursor-pointer transition-colors ${addFirstRelease ? 'bg-black border-gray-600' : 'bg-black border-gray-600 hover:border-red-500'}`}
            >
              <input
                type="checkbox"
                checked={addFirstRelease}
                onChange={e => setAddFirstRelease(e.target.checked)}
                className="sr-only"
              />
              <div
                className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${addFirstRelease ? 'bg-red-500 border-red-500' : 'bg-transparent border-gray-600 group-hover:border-red-500'}`}
              >
                {addFirstRelease && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="font-medium text-white">Add First Release to Title</span>
            </label>

            {addFirstRelease && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Release Details</h3>

                <div className="bg-black border border-red-800 p-4 rounded-lg mb-4">
                  {/* Release Title row */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Release Title *"
                      title="Release Title"
                      value={releaseFormData.release_title}
                      onChange={e =>
                        setReleaseFormData({ ...releaseFormData, release_title: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                      required
                    />
                  </div>

                  {/* Distributor Info and Distributor Code row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder="Distributor Info"
                      title="Distributor Info"
                      value={releaseFormData.studio}
                      onChange={e =>
                        setReleaseFormData({ ...releaseFormData, studio: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Distributor Code"
                      title="Distributor Code"
                      value={releaseFormData.distributor_code || ''}
                      onChange={e =>
                        setReleaseFormData({ ...releaseFormData, distributor_code: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                    />
                  </div>

                  {/* UPC and UPC Distributor row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <input
                        type="text"
                        placeholder="UPC (12 digits)"
                        title="UPC"
                        value={releaseFormData.upc}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                          setReleaseFormData({ ...releaseFormData, upc: value });
                        }}
                        className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                        maxLength="12"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="UPC Distributor"
                        title="UPC Distributor"
                        value={releaseFormData.upc_distributor}
                        onChange={e =>
                          setReleaseFormData({
                            ...releaseFormData,
                            upc_distributor: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                        maxLength="100"
                      />
                    </div>
                  </div>

                  {/* Year, Country, Region row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder="Year"
                      title="Year"
                      value={releaseFormData.release_year || ''}
                      onChange={e => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setReleaseFormData({ ...releaseFormData, release_year: value });
                      }}
                      className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                      maxLength="4"
                      pattern="[0-9]{4}"
                    />
                    <input
                      type="text"
                      placeholder="Country"
                      title="Country"
                      value={releaseFormData.release_country || 'United States'}
                      onChange={e =>
                        setReleaseFormData({ ...releaseFormData, release_country: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                    />
                    <CustomSelect
                      title="Region"
                      value={releaseFormData.region || 'NTSC'}
                      onChange={v => setReleaseFormData({ ...releaseFormData, region: v })}
                      options={[
                        { value: 'NTSC', label: 'NTSC' },
                        { value: 'PAL', label: 'PAL' },
                        { value: 'SECAM', label: 'SECAM' },
                      ]}
                    />
                  </div>

                  {/* Format, Case, Speed row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <CustomSelect
                        title="Format"
                        value={releaseFormData.format || 'VHS'}
                        onChange={v => setReleaseFormData({ ...releaseFormData, format: v })}
                        options={[
                          { value: 'VHS', label: 'VHS' },
                          { value: 'Betamax', label: 'Betamax' },
                          { value: 'LaserDisc', label: 'LaserDisc' },
                          { value: 'DVD', label: 'DVD' },
                          { value: 'Blu-ray', label: 'Blu-ray' },
                        ]}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        title="Case Type"
                        value={releaseFormData.case_type || 'Slipcase'}
                        onChange={v => setReleaseFormData({ ...releaseFormData, case_type: v })}
                        options={[
                          { value: 'Slipcase', label: 'Slipcase' },
                          { value: 'Clamshell', label: 'Clamshell' },
                          { value: 'Book Box', label: 'Book Box' },
                          { value: 'Big Box', label: 'Big Box' },
                          { value: 'Standard', label: 'Standard' },
                          { value: 'Cut Box', label: 'Cut Box' },
                        ]}
                      />
                    </div>
                    <div>
                      <CustomSelect
                        title="Speed"
                        value={releaseFormData.speed || 'SP'}
                        onChange={v => setReleaseFormData({ ...releaseFormData, speed: v })}
                        options={[
                          { value: 'SP', label: 'SP' },
                          { value: 'LP', label: 'LP' },
                          { value: 'EP/SLP', label: 'EP/SLP' },
                        ]}
                      />
                    </div>
                  </div>

                  {/* Edition dropdown and other edition text field */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <CustomSelect
                      title="Edition"
                      value={releaseFormData.edition || 'Standard'}
                      onChange={v => setReleaseFormData({ ...releaseFormData, edition: v })}
                      options={[
                        { value: 'Standard', label: 'Standard Edition' },
                        { value: 'Screener', label: 'Screener' },
                        { value: 'Promotional', label: 'Promotional' },
                        { value: 'Directors Cut', label: 'Directors Cut' },
                        { value: 'Other', label: 'Other' },
                      ]}
                    />
                    <input
                      type="text"
                      placeholder="Other Edition when selected"
                      title="Other Edition"
                      value={releaseFormData.edition_other || ''}
                      onChange={e =>
                        setReleaseFormData({ ...releaseFormData, edition_other: e.target.value })
                      }
                      readOnly={releaseFormData.edition !== 'Other'}
                      className={`w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none ${releaseFormData.edition !== 'Other' ? 'text-gray-400 cursor-not-allowed' : ''}`}
                    />
                  </div>

                  {/* Release Photos Upload */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-gray-300 mb-2">Release Photos</h5>
                    <p className="text-sm font-bold text-gray-300 mb-3">
                      Only use original photos, or those unwatermarked from the public domain
                    </p>

                    <MultiImageUpload
                      onImagesChange={(images, error) => {
                        setSelectedReleaseImages(images);
                        if (error) console.error('Release photo error:', error);
                      }}
                      currentImages={selectedReleaseImages}
                      error={null}
                      maxImages={5}
                    />
                  </div>

                  <textarea
                    placeholder="Release Notes"
                    title="Release Notes"
                    value={releaseFormData.release_notes}
                    onChange={e =>
                      setReleaseFormData({ ...releaseFormData, release_notes: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Collection / Wantlist Options - Only for new movies */}
        {!editingMasterItem && (
          <div className="mb-6">
            <div className="flex gap-3">
              <label
                className={`group flex-1 flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${addToMyCollection ? 'bg-black border-gray-600' : 'bg-black border-gray-600 hover:border-red-500'}`}
              >
                <input
                  type="checkbox"
                  checked={addToMyCollection}
                  onChange={e => setAddToMyCollection(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${addToMyCollection ? 'bg-red-500 border-red-500' : 'bg-transparent border-gray-600 group-hover:border-red-500'}`}
                >
                  {addToMyCollection && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <span className="font-medium text-white text-sm">Add to Collection</span>
                  <p className="text-xs text-gray-400 mt-0.5">
                    If no release added, will save title without release
                  </p>
                </div>
              </label>
              <label
                className={`group flex-1 flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${addToMyWantlist ? 'bg-black border-gray-600' : 'bg-black border-gray-600 hover:border-red-500'}`}
              >
                <input
                  type="checkbox"
                  checked={addToMyWantlist}
                  onChange={e => setAddToMyWantlist(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${addToMyWantlist ? 'bg-red-500 border-red-500' : 'bg-transparent border-gray-600 group-hover:border-red-500'}`}
                >
                  {addToMyWantlist && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="font-medium text-white text-sm">Add to Wantlist</span>
              </label>
            </div>

            {addToMyCollection && (
              <div className="bg-black border border-gray-600 p-4 rounded-lg mt-3 space-y-4">
                {!addFirstRelease && (
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Format</p>
                    <div className="flex gap-2 flex-wrap">
                      {['VHS', 'Betamax', 'LaserDisc', 'DVD', 'Blu-ray'].map(fmt => (
                        <button
                          key={fmt}
                          type="button"
                          onClick={() =>
                            setNewCollectionItem({ ...newCollectionItem, format: fmt })
                          }
                          className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                            (newCollectionItem?.format || 'VHS') === fmt
                              ? 'border-red-500 text-red-500'
                              : 'border-gray-600 text-gray-400 hover:border-red-500 hover:text-red-500'
                          }`}
                        >
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <textarea
                  placeholder="Personal notes"
                  value={newCollectionItem?.personal_notes || ''}
                  onChange={e =>
                    setNewCollectionItem({ ...newCollectionItem, personal_notes: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                />
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-400 hover:text-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <LoadingButton
            onClick={handleSave}
            isLoading={isSubmitting}
            disabled={
              !newMasterItem.title ||
              (!editingMasterItem && !addFirstRelease && !newMasterItem.released)
            }
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {editingMasterItem ? 'Update' : 'Add Title'}
          </LoadingButton>
        </div>
      </BaseModal>
    </>
  );
};

export default AddMediaModal;
